import React, { useState, useEffect } from 'react';

import { Input } from 'src/app/components/lib/input';
import { SelectAdornment } from 'src/app/components/lib/input/select-adornment';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';

import { formatTimeInput, validateTime } from './wizard-refactor/wizard/shared/helpers';

type TimePickerProps = {
  automationId?: string;
  disabled?: boolean;
  errorMessage?: string;
  setTime: (time: string) => void;
  time: string;
};
export function TimePicker({ automationId, time, setTime, disabled = false, errorMessage }: TimePickerProps) {
  const useTimePickerValidation = useLDFlag(LD.TIME_PICKER_VALIDATION_ROLLOUT, false);
  const getAmpmFromString = (string: string) => (string.split(' ')[1]?.toUpperCase() === 'AM' ? 'AM' : 'PM');
  const getHourMinuteFromString = (string: string) => string.split(' ')[0] ?? '12:00';
  const [ampm, setAmpm] = useState(getAmpmFromString(time));
  const [hourMinute, setHourMinute] = useState(getHourMinuteFromString(time));
  const [error, setError] = useState('');

  useEffect(() => {
    if (time !== '') {
      setAmpm(getAmpmFromString(time));
      setHourMinute(getHourMinuteFromString(time));
    }
  }, [time]);

  useEffect(() => {
    if (hourMinute !== '') {
      setTime(`${hourMinute} ${ampm}`);
    } else {
      setTime('');
    }
    // setTime will cause infinite render loop if it's in the deps array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ampm, hourMinute]);

  const testIdHourProps = automationId ? { automationId } : {};

  const handleTimeChange = (value: string) => {
    if (useTimePickerValidation) {
      const formatted = formatTimeInput(value);
      setHourMinute(formatted);
      const validationError = validateTime(formatted);
      setError(validationError);
    } else {
      setHourMinute(value);
    }
  };

  return (
    <Input
      {...testIdHourProps}
      disabled={disabled}
      endAdornment={
        <SelectAdornment
          disabled={disabled}
          disableNoneOption
          label='Time'
          options={[
            { value: 'AM', label: 'AM' },
            { value: 'PM', label: 'PM' },
          ]}
          value={ampm}
          onChange={({ target }) => setAmpm(target.value)}
        />
      }
      errorMessage={useTimePickerValidation ? error || errorMessage : undefined}
      inputProps={useTimePickerValidation ? { pattern: '(1[0-2]|[1-9]):[0-5][0-9]' } : undefined}
      placeholder={useTimePickerValidation ? 'H:MM (e.g., 9:40)' : undefined}
      value={hourMinute}
      onChange={({ target }) => handleTimeChange(target.value)}
    />
  );
}
